import { useCallback, useState, type Dispatch, type SetStateAction } from "react";
import { getClientErrorMessage } from "common/src/utils/errorUtils";
import type { DiscoverGameV2Response, GameLinkKind } from "libs/apiClient";
import { DiscoverGameLinkItemResponse } from "libs/apiClient";
import client from "libs/client";
import { useToast } from "ui-kit";
import { _ } from "i18n";
import { DiscoverGameLinksFields } from "../../links/DiscoverGameLinksFields";
import {
	createLinkEntry,
	linksFromApiItems,
	removeLinkAt,
	type LinkEntry,
} from "../../links/discoverGameLinks";
import { DISCOVER_GAME_V2_DETAIL_QUERY_KEY, DiscoverGameLinkKind } from "./constants";

interface DiscoverGameLinksTabProps {
	gameData: DiscoverGameV2Response;
}

function invalidateDiscoverGameDetail(): void {
	client.queryClient.invalidateQueries({
		predicate: (query) => query.queryKey.includes(DISCOVER_GAME_V2_DETAIL_QUERY_KEY),
	});
}

function updateLinkValueAt(
	setLinks: Dispatch<SetStateAction<LinkEntry[]>>,
	index: number,
	value: string,
): void {
	setLinks((prev) => {
		const links = [...prev];
		links[index] = { ...links[index], value };
		return links;
	});
}

export function DiscoverGameLinksTab({ gameData }: DiscoverGameLinksTabProps) {
	const { addSuccessToast, addErrorToast } = useToast();
	const [teamsheetLinks, setTeamsheetLinks] = useState<LinkEntry[]>(() =>
		linksFromApiItems(gameData.teamsheetLinks));
	const [videoLinks, setVideoLinks] = useState<LinkEntry[]>(() =>
		linksFromApiItems(gameData.videoLinks));
	const [savingCount, setSavingCount] = useState(0);

	const handleMutationError = useCallback(
		(error: unknown) => {
			addErrorToast(getClientErrorMessage(error) ?? _("discover.gameDetail.toast.updateFailed"));
		},
		[addErrorToast],
	);

	const saveLink = useCallback(
		async (
			index: number,
			kind: GameLinkKind,
			getEntries: () => LinkEntry[],
			setLinks: Dispatch<SetStateAction<LinkEntry[]>>,
		) => {
			const entry = getEntries()[index];
			if (entry === undefined) {
				return;
			}
			const trimmed = entry.value.trim();
			if (trimmed.length === 0) {
				return;
			}

			setSavingCount((c) => c + 1);
			try {
				if (entry.gameLinkId !== undefined) {
					await client.patchDiscoverGameLink.mutation({
						linkId: entry.gameLinkId,
						body: { link: trimmed },
					});
				} else {
					const response = await client.postDiscoverGameLink.mutation({
						id: gameData.id,
						body: { link: trimmed, kind },
					});
					const created = DiscoverGameLinkItemResponse.parse(response?.data);
					setLinks((prev) => {
						const links = [...prev];
						links[index] = {
							...links[index],
							gameLinkId: created.id,
							value: created.link,
						};
						return links;
					});
				}
				invalidateDiscoverGameDetail();
				addSuccessToast(_("discover.gameDetail.toast.updated"));
			} catch (error: unknown) {
				handleMutationError(error);
			} finally {
				setSavingCount((c) => c - 1);
			}
		},
		[addSuccessToast, gameData.id, handleMutationError],
	);

	const removeLink = useCallback(
		async (
			index: number,
			getEntries: () => LinkEntry[],
			setLinks: Dispatch<SetStateAction<LinkEntry[]>>,
		) => {
			const entry = getEntries()[index];
			if (entry === undefined) {
				return;
			}

			if (entry.gameLinkId === undefined) {
				setLinks((prev) => removeLinkAt(prev, index));
				return;
			}

			setSavingCount((c) => c + 1);
			try {
				await client.deleteDiscoverGameLink.mutation({ linkId: entry.gameLinkId });
				setLinks((prev) => removeLinkAt(prev, index));
				invalidateDiscoverGameDetail();
				addSuccessToast(_("discover.gameDetail.toast.updated"));
			} catch (error: unknown) {
				handleMutationError(error);
			} finally {
				setSavingCount((c) => c - 1);
			}
		},
		[addSuccessToast, handleMutationError],
	);

	const saveTeamsheetLink = useCallback(
		(index: number) => saveLink(index, DiscoverGameLinkKind.Teamsheet, () => teamsheetLinks, setTeamsheetLinks),
		[saveLink, teamsheetLinks],
	);
	const saveVideoLink = useCallback(
		(index: number) => saveLink(index, DiscoverGameLinkKind.Video, () => videoLinks, setVideoLinks),
		[saveLink, videoLinks],
	);
	const removeTeamsheetLink = useCallback(
		(index: number) => removeLink(index, () => teamsheetLinks, setTeamsheetLinks),
		[removeLink, teamsheetLinks],
	);
	const removeVideoLink = useCallback(
		(index: number) => removeLink(index, () => videoLinks, setVideoLinks),
		[removeLink, videoLinks],
	);

	const setTeamsheetLinkAt = useCallback(
		(index: number, value: string) => updateLinkValueAt(setTeamsheetLinks, index, value),
		[],
	);
	const setVideoLinkAt = useCallback(
		(index: number, value: string) => updateLinkValueAt(setVideoLinks, index, value),
		[],
	);

	return (
		<div className="discover-card discoverGameDetailV2-tabCard">
			<DiscoverGameLinksFields
				teamsheetLinks={teamsheetLinks}
				videoLinks={videoLinks}
				onTeamsheetLinkChange={setTeamsheetLinkAt}
				onVideoLinkChange={setVideoLinkAt}
				onAddTeamsheetLink={() => setTeamsheetLinks((prev) => [...prev, createLinkEntry()])}
				onAddVideoLink={() => setVideoLinks((prev) => [...prev, createLinkEntry()])}
				onRemoveTeamsheetLink={removeTeamsheetLink}
				onRemoveVideoLink={removeVideoLink}
				onSaveTeamsheetLink={saveTeamsheetLink}
				onSaveVideoLink={saveVideoLink}
				isSaving={savingCount > 0}
				showSaveIcon
				showRemoveIcon
			/>
		</div>
	);
}
